import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { Lion } from '@/components/brand/Lion'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { ErrorState, Loading } from '@/components/ui/States'
import { supabase, describeError } from '@/lib/supabase'

/**
 * The second factor, asked for at sign-in when the account has one enrolled.
 *
 * Passing this raises the session to aal2, which is what `is_staff()` demands
 * once `mfa_required` is set on the staff row. Until then the session reads
 * nothing at all, so this screen is the whole app for that user.
 */
export function MfaChallenge({ onSignOut }: { onSignOut: () => void }) {
  const queryClient = useQueryClient()
  const [factorId, setFactorId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void supabase.auth.mfa.listFactors().then(({ data, error: listError }) => {
      if (listError) {
        setError(describeError(listError))
        return
      }
      const verified = data?.totp?.find((factor) => factor.status === 'verified')
      setFactorId(verified?.id ?? null)
    })
  }, [])

  async function verify(event: React.FormEvent) {
    event.preventDefault()
    if (!factorId) return

    setBusy(true)
    setError(null)

    const challenge = await supabase.auth.mfa.challenge({ factorId })
    if (challenge.error) {
      setError(describeError(challenge.error))
      setBusy(false)
      return
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code: code.trim(),
    })

    if (verifyError) {
      setError('That code was not accepted. Codes change every 30 seconds — try the current one.')
      setCode('')
      setBusy(false)
      return
    }

    // The assurance level and everything gated behind it are now stale.
    await queryClient.invalidateQueries()
  }

  if (factorId === null && !error) return <Loading label="Checking your account" />

  return (
    <main className="grid min-h-dvh place-items-center px-5 py-12">
      <div className="w-full max-w-[22rem]">
        <div className="mb-9 flex flex-col items-center text-center">
          <Lion size={44} />
          <h1 className="mt-5 font-[family-name:var(--font-heading)] text-[0.875rem] font-medium">
            One more step
          </h1>
          <p className="mt-2 text-[length:var(--text-sm)] text-taupe">
            Enter the six-digit code from your authenticator app.
          </p>
        </div>

        {error ? <ErrorState className="mb-5" title="Could not verify" detail={error} /> : null}

        <form onSubmit={verify} className="flex flex-col gap-5">
          <Field label="Authentication code">
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              className="text-center text-[1.5rem] tracking-[0.4em]"
            />
          </Field>

          <Button type="submit" variant="primary" loading={busy} loadingLabel="Verifying" disabled={code.length !== 6} className="w-full">
            Verify
          </Button>
        </form>

        <button type="button" onClick={onSignOut} className="label mx-auto mt-8 block underline decoration-[var(--color-rule-strong)] underline-offset-4 hover:text-ink">
          Sign out
        </button>
      </div>
    </main>
  )
}
